import { toast } from 'common'
import { visitorCheckin as api, DocumentScanResult, SynergyConnectMetadata } from 'st-app/api'
import { requireVisitorCheckinContext } from '../VisitorCheckinContext'
import { requireStepContext } from '../../st-wizard/st-wizard'
import DocumentReader from '../../st-document-reader/st-document-reader'
import { Show } from 'solid-js'
import Button from '../../st-button/st-button'

const ERROR_ALREADY_CHECKED_IN = 'ALREADY_CHECKED_IN';

let scMetadata: Promise<SynergyConnectMetadata> | null = null;
try {
    scMetadata = api.GetSynergyConnectMetadata();
} catch {
    scMetadata = null;
}

export const ScanIDStep = () => {
    const step = requireStepContext();
    const checkin = requireVisitorCheckinContext();

    const updateVisitor = async (scan: DocumentScanResult) => {
        if (checkin.isUpdatingVisitor())
            return false;

        try {
            checkin.setIsUpdatingVisitor(true);
            const initialScanResponse = await api.RecordInitialScan(checkin.vmGU(), checkin.vmStationGU() ?? '', scan);

            if (initialScanResponse.errorCode === ERROR_ALREADY_CHECKED_IN) {
                checkin.setActiveCheckin(initialScanResponse.activeCheckin);
                return false;
            }

            if (!initialScanResponse.status) {
                toast.notify('error', 'The initial scan was recorded, but no visitor ID was returned from the server. Please try again.');
                return false;
            }
            if (initialScanResponse.repeatTier === 'Grace') {
                // Grace: re-verify + reopen operate on the PRIOR record.
                checkin.setRepeatTier('Grace');
                checkin.setRepeatPriorVisitGU(initialScanResponse.repeatPriorVisit!.vmVisitorGU!);
                checkin.setVmGU(initialScanResponse.repeatPriorVisit!.vmVisitorGU!);
            } else if (initialScanResponse.repeatTier === 'Remembered') {
                checkin.setRepeatTier('Remembered');
                const p = initialScanResponse.repeatPriorVisit!;
                if (p.visitorType) checkin.setVisitorType(p.visitorType);
                if (p.checkInReason) checkin.setReason(p.checkInReason);
                checkin.setMeetingWith(p.meetingWith ?? '');
                checkin.setMeetingWithName(p.meetingWithName ?? '');
            }
            checkin.setVisitorStatus(initialScanResponse.status as 'PEND' | 'ALLOW' | 'DENY');
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while recording the initial scan. Please try again.');
        } finally {
            checkin.setIsUpdatingVisitor(false);
        }
    }

    const checkOutAndProceed = async () => {
        const active = checkin.activeCheckin();
        if (!active?.vmVisitorGU) return;

        try {
            await api.CheckOutExistingVisitor(active.vmVisitorGU);
            checkin.setActiveCheckin(undefined);
            const scan = checkin.scan();
            if (scan) await updateVisitor(scan);
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while checking out the existing visitor. Please try again.');
        }

        const status = checkin.visitorStatus();
        if (status === 'PEND') {
            step.setCompleted(true);
            setTimeout(() => step.next(), 1000);
        }
    }

    const onScan = async (scan: DocumentScanResult) => {
        if (scan && Object.keys(scan?.AAMVA ?? {}).length > 0) {
            checkin.onScan(scan);
            step.setCompleted(true);
        } else {
            step.setCompleted(false);
            checkin.onScan(undefined);
            return;
        }

        checkAndPlaySynergyConnectAudio(scan.AAMVA.idNumber);

        if (!checkin.visitorStatus()) {
            await updateVisitor(scan);
        }

        if (checkin.visitorStatus() === 'PEND') {
            step.setCompleted(true);

            setTimeout(() => {
                step.next();
            }, 1000);
        }
    }

    return (
        <>
            <DocumentReader scan={checkin.scan()} onScan={onScan} ref={(ctx) => checkin.documentReader = ctx} />
            <Show when={checkin.activeCheckin()}>
                {(active) => (
                    <div class="st-visitor-already-checked-in">
                        <p>
                            <strong>{active().firstName} {active().lastName}</strong> is already checked in
                            {active().checkInDatetime ? ` since ${active().checkInDatetime}` : ''}.
                            Would you like to check them out and continue?
                        </p>
                        <Button type='success' icon='fas fa-arrow-right' onClick={checkOutAndProceed}>Check Out and Continue</Button>
                        <Button type='danger' icon='fas fa-xmark' onClick={() => checkin.setActiveCheckin(undefined)}>Cancel</Button>
                    </div>
                )}
            </Show>
        </>
    )
}

const checkAndPlaySynergyConnectAudio = async (dlNumber: string | undefined) => {
    const sc = await scMetadata;
    if (!sc) return;

    let audio: HTMLAudioElement | undefined = undefined;
    if (sc && sc.BGDL && sc.BGDL === dlNumber && sc.vmAudioBad) {
        audio = new Audio(sc.vmAudioBad);
    } else if (sc && sc.GGDL && sc.GGDL === dlNumber && sc.vmAudioGood) {
        audio = new Audio(sc.vmAudioGood);
    }

    if (audio) {
        try {
            await audio.play();
        } catch (e) {
            console.error('Error playing audio:', e);
        }
    }
    
}
